import {
  ArrowRight,
  Check,
  CircleAlert,
  Printer,
  RefreshCw,
  ScanLine,
  Search,
  Weight,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { api, postJSON } from '../api'
import { Status } from '../components/Status'
import { PageError, PageLoading } from '../components/PageState'
import { ObservedContentsEditor } from '../components/ObservedContentsEditor'
import { formatDate, formatDateTime } from '../format'
import type { Category, ContentsItem, FulfillmentQuote, MatchCandidate, Package } from '../types'

export function StaffQueue() {
  const [packages, setPackages] = useState<Package[] | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [selected, setSelected] = useState<Package | null>(null)
  const [intake, setIntake] = useState(false)
  const [error, setError] = useState('')
  const load = () =>
    api<{ packages: Package[]; categories: Category[] }>('/api/staff/packages')
      .then((value) => { setPackages(value.packages); setCategories(value.categories) })
      .catch((cause) => setError(cause.message))

  useEffect(() => {
    load()
  }, [])

  const counts = useMemo(
    () => ({
      intake:
        packages?.filter(
          (value) => value.status === 'unmatched' || value.status === 'inspection',
        ).length || 0,
      verify:
        packages?.filter(
          (value) =>
            value.matchStatus === 'matched' &&
            (!value.customsReady || value.releaseQuoteState === 'pending_acceptance') &&
            !value.exceptionKind,
        ).length || 0,
      ship:
        packages?.filter(
          (value) => value.status === 'ready_to_ship' && !value.exceptionKind,
        ).length || 0,
      exceptions: packages?.filter((value) => value.exceptionKind).length || 0,
    }),
    [packages],
  )

  if (error) return <PageError message={error} />
  if (!packages) return <PageLoading />

  return (
    <div className="page page--staff">
      <header className="page-head">
        <div>
          <div className="eyebrow">
            <span /> WAREHOUSE · NEW YORK
          </div>
          <h1>Routing desk</h1>
          <p>
            Record the parcel you will ship, review its same-service carrier cost,
            then approve the label release.
          </p>
        </div>
        <button className="button button--dark" onClick={() => setIntake(true)}>
          <ScanLine size={17} /> Intake parcel
        </button>
      </header>

      <div className="ops-stats">
        <div>
          <span>NEEDS INTAKE</span>
          <b>{counts.intake}</b>
          <small>Match or inspect</small>
        </div>
        <div>
          <span>NEEDS RELEASE REVIEW</span>
          <b>{counts.verify}</b>
          <small>Record final facts</small>
        </div>
        <div>
          <span>READY TO SHIP</span>
          <b>{counts.ship}</b>
          <small>Purchased labels</small>
        </div>
        <div
          className={`ops-stats__health ${counts.exceptions ? 'ops-stats__health--hold' : ''}`}
        >
          <span>
            <i /> {counts.exceptions ? 'RELEASE HOLD' : 'SYSTEM'}
          </span>
          <b>{counts.exceptions ? `${counts.exceptions} need review` : 'Providers ready'}</b>
          <small>
            {counts.exceptions ? 'Automatic purchase blocked' : 'Payments · Shippo'}
          </small>
        </div>
      </div>

      <section className="queue-panel">
        <div className="queue-toolbar">
          <div>
            <h2>Active parcels</h2>
            <span>{packages.length} records</span>
          </div>
          <label className="search">
            <Search size={17} />
            <input placeholder="RXID, tracking, or name" />
          </label>
          <button className="icon-button" onClick={load} aria-label="Refresh">
            <RefreshCw size={17} />
          </button>
        </div>
        <div className="queue-table">
          <div className="queue-table__head">
            <span>Parcel</span>
            <span>Customer</span>
            <span>Intake state</span>
            <span>Received</span>
            <span />
          </div>
          {packages.map((item) => (
            <button
              className="queue-table__row"
              key={item.id}
              onClick={() => setSelected(item)}
            >
              <span>
                <b>{item.rxid || 'NO RXID'}</b>
                <small>{item.inboundTrackingCode || 'No tracking recorded'}</small>
              </span>
              <span>
                <b>{item.customerName || 'Unmatched'}</b>
                <small>{item.contentsSummary || 'Needs review'}</small>
              </span>
              <Status
                value={
                  item.exceptionKind
                    ? `${item.exceptionKind} hold`
                    : item.matchStatus === 'matched'
                      ? item.status
                      : item.matchStatus
                }
              />
              <span>{formatDate(item.receivedAt)}</span>
              <ArrowRight size={17} />
            </button>
          ))}
          {!packages.length && (
            <div className="queue-empty">
              <Check />
              <b>Queue clear</b>
              <span>New intake records will appear here.</span>
            </div>
          )}
        </div>
      </section>

      {intake && (
        <IntakeModal
          close={() => setIntake(false)}
          done={() => {
            setIntake(false)
            load()
          }}
        />
      )}
      {selected && (
        <PackageDrawer
          item={selected}
          categories={categories}
          close={() => setSelected(null)}
          updated={() => {
            setSelected(null)
            load()
          }}
        />
      )}
    </div>
  )
}

function IntakeModal({ close, done }: { close: () => void; done: () => void }) {
  const [form, setForm] = useState({
    observedRxid: '',
    observedRecipientName: '',
    inboundCarrier: '',
    inboundTrackingCode: '',
  })
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<Package | null>(null)
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    try {
      setResult(await postJSON<Package>('/api/staff/intake', form))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <div className="modal">
        <div className="modal__head">
          <div>
            <span className="mono-label">PHYSICAL INTAKE</span>
            <h2>Scan or enter the label.</h2>
          </div>
          <button onClick={close}>×</button>
        </div>
        {result ? (
          <div className="intake-result">
            <span
              className={result.matchStatus === 'matched' ? 'success-mark' : 'warning-mark'}
            >
              {result.matchStatus === 'matched' ? <Check /> : <CircleAlert />}
            </span>
            <h3>
              {result.matchStatus === 'matched'
                ? `Matched to RXID ${result.rxid}`
                : 'Held for manual match'}
            </h3>
            <p>
              {result.matchStatus === 'matched'
                ? 'The parcel can move to verification.'
                : 'No unique RXID or tracking match was found. The parcel remains unassigned.'}
            </p>
            <button className="button button--primary button--full" onClick={done}>
              Add to queue
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <label>
              RXID <small>Preferred match</small>
              <input
                value={form.observedRxid}
                onChange={(event) =>
                  setForm({ ...form, observedRxid: event.target.value.toUpperCase() })
                }
                maxLength={4}
                placeholder="7K4M"
                autoFocus
              />
            </label>
            <label>
              Recipient name
              <input
                value={form.observedRecipientName}
                onChange={(event) =>
                  setForm({ ...form, observedRecipientName: event.target.value })
                }
                placeholder="As printed on label"
              />
            </label>
            <div className="form-grid">
              <label>
                Inbound carrier
                <input
                  value={form.inboundCarrier}
                  onChange={(event) =>
                    setForm({ ...form, inboundCarrier: event.target.value })
                  }
                  placeholder="UPS"
                />
              </label>
              <label>
                Tracking code
                <input
                  value={form.inboundTrackingCode}
                  onChange={(event) =>
                    setForm({ ...form, inboundTrackingCode: event.target.value })
                  }
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="button" className="button button--ghost" onClick={close}>
                Cancel
              </button>
              <button className="button button--dark" disabled={busy}>
                {busy ? 'Matching…' : 'Match parcel'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

function ManualMatch({ item, updated }: { item: Package; updated: () => void }) {
  const [query, setQuery] = useState(item.customerName)
  const [candidates, setCandidates] = useState<MatchCandidate[]>([])
  const [selected, setSelected] = useState<MatchCandidate | null>(null)
  const [searched, setSearched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const search = async () => {
    setBusy(true)
    setError('')
    setSelected(null)
    try {
      const result = await api<{ candidates: MatchCandidate[] }>(
        `/api/staff/match-candidates?q=${encodeURIComponent(query)}`,
      )
      setCandidates(result.candidates)
      setSearched(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Candidate search failed')
    } finally {
      setBusy(false)
    }
  }
  const confirm = async () => {
    if (!selected) return
    setBusy(true)
    setError('')
    try {
      await postJSON(`/api/staff/packages/${item.id}/match`, {
        expectedInboundId: selected.inboundId,
      })
      updated()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not match package')
      setBusy(false)
    }
  }

  return (
    <div className="match-review">
      <CircleAlert />
      <h3>Ownership not confirmed</h3>
      <p>
        No exact RXID or tracking match was found. Search the expected-package list,
        compare each detail with the printed label, then confirm one record.
      </p>
      <label>
        Customer name, RXID, order, or tracking
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search expected packages"
        />
      </label>
      <button
        className="button button--outline button--full"
        disabled={busy || query.trim().length < 2}
        onClick={search}
      >
        <Search size={16} /> {busy ? 'Searching…' : 'Find candidates'}
      </button>
      {error && <div className="form-error">{error}</div>}
      {selected ? (
        <div className="match-confirm">
          <span className="mono-label">CONFIRM OWNERSHIP CHANGE</span>
          <h4>
            {selected.customerName} · RXID {selected.rxid}
          </h4>
          <dl>
            <div><dt>Expected item</dt><dd>{selected.contentsSummary}</dd></div>
            <div><dt>Retailer</dt><dd>{selected.retailer || 'Not provided'}</dd></div>
            <div><dt>Retailer order</dt><dd>{selected.retailerOrderRef || 'Not provided'}</dd></div>
            <div><dt>Inbound tracking</dt><dd>{selected.inboundTrackingCode || 'Not provided'}</dd></div>
            <div><dt>Declared</dt><dd>{formatDate(selected.createdAt)}</dd></div>
          </dl>
          <p>
            This assigns the physical parcel to this customer and retires the
            package-specific RXID.
          </p>
          <div className="form-actions">
            <button className="button button--ghost" disabled={busy} onClick={() => setSelected(null)}>
              Back to candidates
            </button>
            <button className="button button--dark" disabled={busy} onClick={confirm}>
              {busy ? 'Assigning…' : `Confirm RXID ${selected.rxid}`}
            </button>
          </div>
        </div>
      ) : (
        <div className="candidate-list">
          {candidates.map((candidate) => (
            <button
              key={candidate.inboundId}
              disabled={busy}
              onClick={() => setSelected(candidate)}
              aria-label={`Review RXID ${candidate.rxid} for ${candidate.customerName}`}
            >
              <span>
                <b>{candidate.customerName}</b>
                <small>{candidate.contentsSummary}</small>
                <small>
                  {candidate.retailer || 'No retailer'} · order{' '}
                  {candidate.retailerOrderRef || 'not provided'}
                </small>
                <small>
                  {candidate.inboundTrackingCode || 'No tracking'} · declared{' '}
                  {formatDate(candidate.createdAt)}
                </small>
              </span>
              <strong>Review RXID {candidate.rxid}</strong>
            </button>
          ))}
          {searched && !candidates.length && (
            <p>No open expected package matches that search. Keep this parcel on hold.</p>
          )}
        </div>
      )}
      <small className="manual-warning">
        A name only narrows the list. No ownership changes until staff confirms the
        reviewed record.
      </small>
    </div>
  )
}

function ProviderException({ item, updated }: { item: Package; updated: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const run = async (path: string, fallback: string) => {
    setBusy(true)
    setError('')
    try {
      await postJSON(path, {})
      updated()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : fallback)
      setBusy(false)
    }
  }
  const artifact = item.exceptionKind === 'artifact'
  const label = item.exceptionKind === 'label'
  const rate = item.exceptionKind === 'rate'
  const title = artifact
    ? 'Private label copy failed'
    : label
      ? 'Shippo outcome unknown'
      : rate
        ? 'Selected carrier service unavailable'
        : item.exceptionKind === 'payment'
          ? 'Payment outcome unknown'
          : 'Order release blocked'
  const copy = artifact
    ? 'Postage was purchased, but archiving the printable label failed. Requeue only this existing label file; do not buy another label.'
    : label
      ? 'The label request may have succeeded even though its response was lost. Reconcile the original Shippo rate and transaction before any new purchase.'
      : rate
        ? item.exceptionMessage
        : item.exceptionKind === 'payment'
          ? 'Do not start a second payment or label purchase until the payment gateway confirms the charge outcome.'
          : 'A refund, void, cancellation, or staff hold blocks dispatch until the provider records and order ledger agree.'

  return (
    <div className="provider-hold">
      <CircleAlert />
      <span className="mono-label">RELEASE BLOCKED</span>
      <h3>{title}</h3>
      <p>{copy}</p>
      {label && (
        <button
          className="button button--dark button--full"
          disabled={busy}
          onClick={() =>
            run(
              `/api/staff/orders/${item.forwardingOrderId}/reconcile-label`,
              'Reconciliation did not confirm a label',
            )
          }
        >
          <RefreshCw size={16} />
          {busy ? 'Checking original shipment…' : 'Reconcile original shipment'}
        </button>
      )}
      {artifact && (
        <button
          className="button button--dark button--full"
          disabled={busy}
          onClick={() =>
            run(
              `/api/staff/orders/${item.forwardingOrderId}/retry-label-artifact`,
              'Could not requeue the private label copy',
            )
          }
        >
          <RefreshCw size={16} />
          {busy ? 'Requeuing label file…' : 'Retry private label copy'}
        </button>
      )}
      {error && <div className="form-error">{error}</div>}
      <small>
        This hold is deliberate. Provider and audit records remain attached to the
        forwarding order.
      </small>
    </div>
  )
}

type VerifyForm = {
  actualWeightGrams: number
  lengthMm: number
  widthMm: number
  heightMm: number
  items: ContentsItem[]
  inspectionNotes: string
}

function PackageDrawer({ item, categories, close, updated }: { item: Package; categories: Category[]; close: () => void; updated: () => void }) {
  const drawerRef = useRef<HTMLElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  const international = item.customsRequired
  const [form, setForm] = useState<VerifyForm>({
    actualWeightGrams: item.actualWeightGrams ?? 0,
    lengthMm: item.lengthMm ?? 0,
    widthMm: item.widthMm ?? 0,
    heightMm: item.heightMm ?? 0,
    items: (item.items.length ? item.items : item.presetItems).map(line=>({...line})),
    inspectionNotes: item.inspectionNotes || '',
  })
  const [busy, setBusy] = useState(false)
  const [releaseQuote, setReleaseQuote] = useState<FulfillmentQuote | null>(item.releaseQuoteId ? {
    id: item.releaseQuoteId,
    orderId: item.forwardingOrderId,
    packageId: item.id,
    verificationRevision: item.verificationRevision,
    carrier: item.carrier,
    service: item.service,
    baselineCostMinor: item.baselineCarrierCostMinor,
    finalCostMinor: item.finalCarrierCostMinor,
    deltaMinor: item.carrierCostDeltaMinor,
    currency: item.carrierCostCurrency,
    state: item.releaseQuoteState as FulfillmentQuote['state'],
    createdAt: item.releaseQuotedAt || '',
    expiresAt: '',
  } : null)
  const [verifiedRecord, setVerifiedRecord] = useState<Package | null>(null)
  const [editingFacts, setEditingFacts] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    openerRef.current = document.activeElement as HTMLElement | null
    drawerRef.current?.focus()
    return () => openerRef.current?.focus()
  }, [])

  const keepFocusInDrawer = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      close()
      return
    }
    if (event.key !== 'Tab') return
    const controls = Array.from(
      drawerRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
      ) ?? [],
    ).filter((element) => element.offsetParent !== null)
    if (!controls.length) return
    const first = controls[0]
    const last = controls[controls.length - 1]
    if (event.shiftKey && (document.activeElement === first || document.activeElement === drawerRef.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }
  const verify = async () => {
    setBusy(true)
    setError('')
    try {
      const quote = await postJSON<FulfillmentQuote>(
        `/api/staff/packages/${item.id}/verify`,
        form,
      )
      setReleaseQuote(quote)
      setEditingFacts(false)
      const queue = await api<{ packages: Package[] }>('/api/staff/packages')
      setVerifiedRecord(queue.packages.find((record) => record.id === item.id) ?? null)
      requestAnimationFrame(() => { if (drawerRef.current) drawerRef.current.scrollTop = 0 })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not verify this parcel')
    } finally {
      setBusy(false)
    }
  }

  const acceptReleaseQuote = async () => {
    if (!releaseQuote) return
    setBusy(true)
    setError('')
    try {
      await postJSON(
        `/api/staff/packages/${item.id}/release-quotes/${releaseQuote.id}/accept`,
        {},
      )
      const queue = await api<{ packages: Package[] }>('/api/staff/packages')
      const record = queue.packages.find((value) => value.id === item.id) ?? null
      setVerifiedRecord(record)
      if (record) {
        setReleaseQuote({ ...releaseQuote, state: record.releaseQuoteState as FulfillmentQuote['state'] })
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not approve this release quote')
    } finally {
      setBusy(false)
    }
  }

  let content
  if (item.matchStatus !== 'matched') {
    content = <ManualMatch item={item} updated={updated} />
  } else if (item.exceptionKind && item.exceptionKind !== 'preset') {
    content = <ProviderException item={item} updated={updated} />
  } else if ((verifiedRecord ?? item).trackingCode) {
    const record = verifiedRecord ?? item
    content = (
      <div className="rate-ready">
        <span className="success-mark"><Check /></span>
        <h3>{record.labelAvailable ? 'Label ready for dispatch' : 'Label purchased; private file preparing'}</h3>
        <p>
          {record.labelAvailable
            ? `${record.carrier} ${record.service}`
            : 'Dispatch stays blocked until the private label copy is ready.'}
        </p>
        <VerificationAudit item={record} />
        <CarrierCostLedger item={record} />
        <div className="tracking-strip"><span>TRACKING</span><b>{record.trackingCode}</b></div>
        {record.labelAvailable ? (
          <a
            className="button button--dark button--full"
            href={`/api/staff/packages/${record.id}/label`}
            target="_blank"
            rel="noreferrer"
          >
            <Printer size={17} /> Open printable label
          </a>
        ) : (
          <button className="button button--ghost button--full" onClick={updated}>
            <RefreshCw size={16} /> Refresh label status
          </button>
        )}
      </div>
    )
  } else if (releaseQuote?.state === 'accepted' || (verifiedRecord ?? item).releaseQuoteState === 'accepted') {
    const record = verifiedRecord ?? item
    content = (
      <div className="rate-ready">
        <span className="success-mark"><Check /></span>
        <h3>Release approved</h3>
        <p>The exact reviewed Shippo rate is queued for purchase. No second rate can be substituted.</p>
        <VerificationAudit item={record} />
        <CarrierCostLedger item={record} quote={releaseQuote} />
        <button className="button button--ghost button--full" onClick={updated}>
          <RefreshCw size={16} /> Refresh label status
        </button>
      </div>
    )
  } else if (!editingFacts && (releaseQuote?.state === 'pending_acceptance' || (verifiedRecord ?? item).releaseQuoteState === 'pending_acceptance')) {
    const persistedVerification = verifiedRecord ?? item
    const changed = persistedVerification.verificationResult === 'different'
    content = (
      <div className="verification-result">
        <span className={changed ? 'warning-mark' : 'success-mark'}>
          {changed ? <CircleAlert /> : <Check />}
        </span>
        <span className="mono-label">FINAL CARRIER CHECK</span>
        <h3>{changed ? 'Final facts differ from the paid preset' : 'Final facts match the paid preset'}</h3>
        <p>
          Shippo rerated the exact carrier account and service using the warehouse facts below.
          The customer total stays fixed.
        </p>
        <VerificationComparison item={persistedVerification ?? item} observed={form} />
        {persistedVerification.verifiedAt ? (
          <small className="verification-meta">
            {persistedVerification.verifiedBy || 'Unknown staff ID'} · {formatDateTime(persistedVerification.verifiedAt)}
          </small>
        ) : (
          <small className="verification-meta">Verification saved · reopen this record to load its audit details</small>
        )}
        <CarrierCostLedger item={persistedVerification} quote={releaseQuote} />
        <div className="release-decision">
          <CircleAlert />
          <p>
            Approval locks these final facts and authorizes purchase of this exact rate.
            {releaseQuote && releaseQuote.deltaMinor > 0
              ? ` RX Routing absorbs ${moneyMinor(releaseQuote.deltaMinor, releaseQuote.currency)}.`
              : ' The customer is never charged again.'}
          </p>
        </div>
        {error && <div className="form-error">{error}</div>}
        <button className="button button--dark button--full" disabled={busy} onClick={acceptReleaseQuote}>
          {busy ? 'Approving release…' : 'Approve final cost and buy label'}
        </button>
        <button className="button button--ghost button--full" disabled={busy} onClick={() => setEditingFacts(true)}>
          Edit final shipping facts
        </button>
      </div>
    )
  } else {
    content = (
      <form
        className="verify-form"
        onSubmit={(event) => {
          event.preventDefault()
          verify()
        }}
      >
        {item.verifiedAt && <VerificationAudit item={item} />}
        <div className="drawer-section-title">
          <Weight />
          <div><b>Enter final shipping facts</b><span>The paid preset is a baseline, not a release requirement</span></div>
        </div>
        <PaidPresetSummary item={item} />
        <div className="measure-grid">
          <label>Weight <small>grams</small><input required min="1" type="number" placeholder={String(item.presetWeightGrams)} value={form.actualWeightGrams || ''} onChange={(event) => setForm({ ...form, actualWeightGrams: +event.target.value })} /></label>
          <label>Length <small>mm</small><input required min="1" type="number" placeholder={String(item.presetLengthMm)} value={form.lengthMm || ''} onChange={(event) => setForm({ ...form, lengthMm: +event.target.value })} /></label>
          <label>Width <small>mm</small><input required min="1" type="number" placeholder={String(item.presetWidthMm)} value={form.widthMm || ''} onChange={(event) => setForm({ ...form, widthMm: +event.target.value })} /></label>
          <label>Height <small>mm</small><input required min="1" type="number" placeholder={String(item.presetHeightMm)} value={form.heightMm || ''} onChange={(event) => setForm({ ...form, heightMm: +event.target.value })} /></label>
        </div>
        <ObservedContentsEditor items={form.items} categories={categories} international={international} onChange={items=>setForm({...form,items})}/>
        <label>Inspection notes<textarea value={form.inspectionNotes} onChange={(event) => setForm({ ...form, inspectionNotes: event.target.value })} /></label>
        {error && <div className="form-error">{error}</div>}
        <button className="button button--dark button--full" disabled={busy}>
          {busy ? 'Getting final carrier cost…' : 'Save facts and review final cost'}
        </button>
      </form>
    )
  }

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && close()}
    >
      <aside
        className="drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`package-drawer-title-${item.id}`}
        tabIndex={-1}
        onKeyDown={keepFocusInDrawer}
      >
        <div className="drawer__head">
          <div><span className="mono-label">WAREHOUSE RELEASE</span><h2 id={`package-drawer-title-${item.id}`}>Verify package for shipment</h2><p className="drawer__head-meta"><span>{item.customerName || 'Manual review'}</span><span>RXID {item.rxid || 'UNMATCHED'}</span>{item.orderPublicId&&<span>{item.orderPublicId}</span>}{item.destination?.city&&<span>{item.destination.city}, {item.destination.countryCode}</span>}</p></div>
          <button onClick={close} aria-label="Close package">×</button>
        </div>
        <div className="drawer__content">
          <WarehouseOrderContext item={item} />
          {content}
        </div>
      </aside>
    </div>
  )
}

function WarehouseOrderContext({ item }: { item: Package }) {
  const destination = item.destination || ({} as Package['destination'])
  const destinationLocality = [destination.city, destination.subdivision, destination.postalCode].filter(Boolean).join(', ')
  const line1 = destination.line1?.trim() || ''
  const streetNumber = destination.streetNumber?.trim() || ''
  const destinationStreet = streetNumber && !line1.toLocaleLowerCase().startsWith(streetNumber.toLocaleLowerCase())
    ? `${streetNumber} ${line1}`.trim()
    : line1 || streetNumber
  const currency = item.carrierCostCurrency || item.presetItems[0]?.currency || 'USD'
  return (
    <section className="warehouse-order-context" aria-label="Order and destination details">
      <header>
        <div><span>RXID</span><b>{item.rxid || 'UNMATCHED'}</b></div>
        <div><span>ORDER</span><b>{item.orderPublicId || 'No linked order'}</b></div>
        <div><span>RECEIVED</span><b>{formatDate(item.receivedAt)}</b></div>
      </header>
      <div className="warehouse-order-context__grid">
        <div><span>CUSTOMER</span><b>{item.customerName || 'Unmatched'}</b><small>{item.contentsSummary || 'Category not recorded'}</small></div>
        <div>
          <span>SHIP TO</span>
          <b>{destination.recipientName || item.customerName || 'Recipient not recorded'}</b>
          <address>
            {destination.company ? <>{destination.company}<br /></> : null}
            {destinationStreet || 'Street address not recorded'}
            {destination.line2 ? <><br />{destination.line2}</> : null}
            {destination.line3 ? <><br />{destination.line3}</> : null}
            {destinationLocality ? <><br />{destinationLocality}</> : null}
            {destination.countryCode ? <><br />{destination.countryCode}</> : null}
          </address>
          {destination.phone ? <small>Phone {destination.phone}</small> : null}
          {destination.email ? <small>{destination.email}</small> : null}
        </div>
        <div><span>SELECTED SERVICE</span><b>{item.carrier && item.service ? `${item.carrier} · ${item.service}` : 'Carrier service not selected'}</b><small>{item.inboundTrackingCode ? `Inbound tracking ${item.inboundTrackingCode}` : 'No inbound tracking recorded'}</small></div>
        <div><span>AUTHORIZED TOTAL</span><b>{item.customerTotalMinor ? moneyMinor(item.customerTotalMinor, currency) : 'Not available'}</b><small>Customer total stays fixed after warehouse rerating</small></div>
      </div>
    </section>
  )
}

function VerificationAudit({ item }: { item: Package }) {
  if (!item.verifiedAt) return null
  return (
    <div className="verification-audit">
      <div><span className="mono-label">FINAL FACTS</span><b>{item.verificationResult === 'matched' ? 'Matches paid preset' : 'Warehouse override'}</b></div>
      <VerificationComparison item={item} observed={{
        actualWeightGrams: item.actualWeightGrams ?? 0,
        lengthMm: item.lengthMm ?? 0,
        widthMm: item.widthMm ?? 0,
        heightMm: item.heightMm ?? 0,
        items: item.items,
        inspectionNotes: item.inspectionNotes,
      }} />
      <small>{item.verifiedBy || 'Unknown staff ID'} · {formatDateTime(item.verifiedAt)}</small>
      {item.inspectionNotes && <p>{item.inspectionNotes}</p>}
    </div>
  )
}

function PaidPresetSummary({ item }: { item: Package }) {
  return (
    <div className="paid-preset-summary">
      <span className="mono-label">CUSTOMER-PAID BASELINE</span>
      <div>
        <span><small>WEIGHT</small><b>{item.presetWeightGrams} g</b></span>
        <span><small>DIMENSIONS</small><b>{item.presetLengthMm} × {item.presetWidthMm} × {item.presetHeightMm} mm</b></span>
        <span><small>QUANTITY</small><b>{item.presetItems.reduce((sum,line)=>sum+line.quantity,0)}</b></span>
      </div>
      <p>Enter what is actually packed below. Different values are allowed and will be rerated before purchase.</p>
    </div>
  )
}

function CarrierCostLedger({ item, quote }: { item: Package; quote?: FulfillmentQuote | null }) {
  const baseline = quote?.baselineCostMinor ?? item.baselineCarrierCostMinor
  const final = quote?.finalCostMinor ?? item.finalCarrierCostMinor
  const delta = quote?.deltaMinor ?? item.carrierCostDeltaMinor
  const currency = quote?.currency || item.carrierCostCurrency || 'USD'
  if (!baseline && !final) return null
  return (
    <div className="carrier-cost-ledger">
      <div className="carrier-cost-ledger__head">
        <span className="mono-label">CARRIER COST RECONCILIATION</span>
        <b>{quote?.carrier || item.carrier} · {quote?.service || item.service}</b>
      </div>
      <dl>
        <div><dt>Cost in customer quote</dt><dd>{moneyMinor(baseline, currency)}</dd></div>
        <div><dt>Final Shippo cost</dt><dd>{moneyMinor(final, currency)}</dd></div>
        <div className={delta > 0 ? 'is-loss' : delta < 0 ? 'is-savings' : ''}>
          <dt>{delta > 0 ? 'Absorbed by RX Routing' : delta < 0 ? 'RX Routing savings' : 'Carrier-cost difference'}</dt>
          <dd>{delta > 0 ? '+' : delta < 0 ? '−' : ''}{moneyMinor(Math.abs(delta), currency)}</dd>
        </div>
        <div><dt>Customer total</dt><dd>{moneyMinor(item.customerTotalMinor, currency)} · fixed</dd></div>
      </dl>
    </div>
  )
}

function VerificationComparison({ item, observed }: { item: Package; observed: VerifyForm }) {
  const rows = [
    ['Packed weight', `${item.presetWeightGrams} g`, `${observed.actualWeightGrams} g`],
    ['Dimensions', `${item.presetLengthMm} × ${item.presetWidthMm} × ${item.presetHeightMm} mm`, `${observed.lengthMm} × ${observed.widthMm} × ${observed.heightMm} mm`],
  ]
  const codes=Array.from(new Set([...item.presetItems,...observed.items].map(line=>line.categoryCode)))
  for(const code of codes){
    const before=item.presetItems.find(line=>line.categoryCode===code),after=observed.items.find(line=>line.categoryCode===code)
    const name=before?.name||after?.name||code
    rows.push([name, before?`${before.quantity} units · ${before.itemWeightGrams} g`:'Not included', after?`${after.quantity} units · ${after.itemWeightGrams} g`:'Not included'])
    if(item.customsRequired) rows.push([name+' declaration',describeDeclaration(before),describeDeclaration(after)])
  }
  return <div className="verification-compare"><div className="verification-compare__head"><span>FACT</span><span>PAID ESTIMATE</span><span>OBSERVED</span></div>{rows.map(([label,expected,actual])=><div className={expected.toLowerCase()===actual.toLowerCase()?'':'is-mismatch'} key={label}><b>{label}</b><span>{expected}</span><span>{actual}</span></div>)}</div>
}

function describeDeclaration(line: ContentsItem | undefined) {
  if (!line) return 'Not included'
  return [
    line.description,
    `${moneyMinor(line.unitValueMinor, line.currency)}/unit`,
    line.originCountryCode,
    `HS ${line.hsCode || 'not specified'}`,
    line.skuCode ? `SKU ${line.skuCode}` : '',
    line.manufacturerCode ? `Manufacturer ${line.manufacturerCode}` : '',
    line.euExemptCategory ? 'EU identifier exemption' : '',
  ].filter(Boolean).join(' · ')
}

function moneyMinor(value: number, currency: string) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(value / 100)
}
